
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, User, Mail, Phone, Car } from "lucide-react";

interface CustomerInfoFormProps {
  customerName: string;
  setCustomerName: (name: string) => void;
  customerEmail: string;
  setCustomerEmail: (email: string) => void;
  phoneNumber?: string;
  setPhoneNumber?: (phone: string) => void;
  address?: string;
  setAddress?: (address: string) => void;
  city?: string;
  setCity?: (city: string) => void;
  state?: string;
  setState?: (state: string) => void;
  zipCode?: string;
  setZipCode?: (zipCode: string) => void;
  licensePlate?: string;
  setLicensePlate?: (licensePlate: string) => void;
  cascadeAnglersClient?: boolean | null;
  setCascadeAnglersClient?: (val: boolean) => void;
}

export const CustomerInfoForm: React.FC<CustomerInfoFormProps> = ({
  customerName,
  setCustomerName,
  customerEmail,
  setCustomerEmail,
  phoneNumber = "",
  setPhoneNumber = () => {},
  address = "",
  setAddress = () => {},
  city = "",
  setCity = () => {},
  state = "",
  setState = () => {},
  zipCode = "",
  setZipCode = () => {},
  licensePlate = "",
  setLicensePlate = () => {},
  cascadeAnglersClient = null,
  setCascadeAnglersClient = () => {}
}) => {
  return (
    <div className="mb-8 space-y-6">
      <div>
        <h3 className="font-semibold text-lg text-tribal-secondary mb-4">Customer Information</h3>
        
        {/* Name and Email section */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="customer-name" className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              Full Name <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input 
              id="customer-name" 
              value={customerName} 
              onChange={e => setCustomerName(e.target.value)} 
              placeholder="Enter your full name" 
              required 
              className={!customerName ? "border-red-300 focus:border-red-500" : ""}
            />
            {!customerName && <p className="text-xs text-red-500 mt-1">Name is required</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="customer-email" className="flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              Email Address <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input 
              id="customer-email" 
              type="email" 
              value={customerEmail} 
              onChange={e => setCustomerEmail(e.target.value)} 
              placeholder="Enter your email address" 
              required 
              className={!customerEmail ? "border-red-300 focus:border-red-500" : ""}
            />
            {!customerEmail && <p className="text-xs text-red-500 mt-1">Email is required</p>}
            <p className="text-xs text-tribal-neutral mt-1">
              Your permit will be sent to this email address
            </p>
          </div>
        </div>
        
        {/* Phone Number */}
        <div className="space-y-2 mt-4">
          <Label htmlFor="phone-number" className="flex items-center">
            <Phone className="w-4 h-4 mr-2" />
            Phone Number <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input 
            id="phone-number" 
            type="tel" 
            value={phoneNumber} 
            onChange={e => setPhoneNumber(e.target.value)} 
            placeholder="(555) 123-4567" 
            required 
          />
        </div>
        
        {/* Address Information */}
        <div className="mt-4">
          <Label className="flex items-center mb-2">
            <MapPin className="w-4 h-4 mr-2" />
            Address Information <span className="text-red-500 ml-1">*</span>
          </Label>
          
          <div className="grid gap-4">
            <div className="space-y-2">
              <Input 
                id="address" 
                value={address} 
                onChange={e => setAddress(e.target.value)} 
                placeholder="Enter street address" 
                required 
              />
            </div>
            
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="city">City <span className="text-red-500 ml-1">*</span></Label>
                <Input 
                  id="city" 
                  value={city} 
                  onChange={e => setCity(e.target.value)} 
                  placeholder="Enter city" 
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State <span className="text-red-500 ml-1">*</span></Label>
                <Input 
                  id="state" 
                  value={state} 
                  onChange={e => setState(e.target.value)} 
                  placeholder="Enter state" 
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip-code">ZIP Code <span className="text-red-500 ml-1">*</span></Label>
                <Input 
                  id="zip-code" 
                  value={zipCode} 
                  onChange={e => setZipCode(e.target.value)} 
                  placeholder="Enter ZIP code" 
                  required 
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* License Plate */}
        <div className="space-y-2 mt-4">
          <Label htmlFor="license-plate" className="flex items-center">
            <Car className="w-4 h-4 mr-2" />
            Vehicle License Plate
          </Label>
          <Input 
            id="license-plate" 
            value={licensePlate} 
            onChange={e => setLicensePlate(e.target.value.toUpperCase())} 
            placeholder="ABC123" 
            className="uppercase" 
          />
          <p className="text-xs text-tribal-neutral mt-1">
            Optional: Helps with vehicle identification
          </p>
        </div>
        
        {/* Cascade Anglers Client Question */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <Label className="text-sm font-medium text-foreground block mb-3">
            Are you a client of the Fly Fishing Guides outfitter, Cascade Anglers? <span className="text-red-500">*</span>
          </Label>
          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="cascadeAnglers"
                checked={cascadeAnglersClient === true}
                onChange={() => setCascadeAnglersClient(true)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">Yes</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="cascadeAnglers"
                checked={cascadeAnglersClient === false}
                onChange={() => setCascadeAnglersClient(false)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">No</span>
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Cascade Anglers is the contracted fishing guide with the SPT Fisheries Program
          </p>
          {cascadeAnglersClient === null && (
            <p className="text-xs text-red-500 mt-1">Please answer this question</p>
          )}
        </div>
      </div>
    </div>
  );
};
